import SalviaLayout from "@/components/SalviaLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Inbox,
  ChevronDown,
  ChevronUp,
  FileText,
  Activity,
  BarChart3,
  Target,
  Info,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  Zap,
  Lightbulb,
  Shield,
  Ban,
  MessageSquareText,
  Eye,
  EyeOff,
  RefreshCw,
} from "lucide-react";
import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useLocation, useParams } from "wouter";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuth } from "@/_core/hooks/useAuth";

interface ModuleBreakdownItem {
  moduleCode: string;
  moduleName: string;
  group: "A" | "B";
  moduleScore: number;
  order: number;
  questions: {
    questionCode: string;
    questionText?: string | null;
    moduleCode: string;
    modeValue: number;
    modeLabel: string | null;
    order: number;
  }[];
}

/**
 * Retorna classes CSS para o qualificador com bom contraste.
 * Resolve o problema de legibilidade do amarelo (Aceitável).
 */
function getQualifierStyles(qualifier: string, colorHex: string | null) {
  const q = (qualifier || "").toLowerCase();

  if (q.includes("satisfat")) {
    return {
      bg: "bg-emerald-50",
      border: "border-emerald-500",
      text: "text-emerald-800",
      badgeBg: "bg-emerald-600",
      badgeText: "text-white",
      iconColor: "#059669",
      accentBg: "bg-emerald-100",
    };
  }
  if (q.includes("aceit")) {
    // Amarelo com alto contraste: fundo âmbar claro, texto âmbar escuro
    return {
      bg: "bg-amber-50",
      border: "border-amber-500",
      text: "text-amber-900",
      badgeBg: "bg-amber-600",
      badgeText: "text-white",
      iconColor: "#b45309",
      accentBg: "bg-amber-100",
    };
  }
  if (q.includes("melhorar") || q.includes("deve")) {
    return {
      bg: "bg-red-50",
      border: "border-red-500",
      text: "text-red-800",
      badgeBg: "bg-red-600",
      badgeText: "text-white",
      iconColor: "#dc2626",
      accentBg: "bg-red-100",
    };
  }
  // Fallback
  return {
    bg: "bg-gray-50",
    border: "border-gray-400",
    text: "text-gray-800",
    badgeBg: "bg-gray-600",
    badgeText: "text-white",
    iconColor: colorHex || "#6b7280",
    accentBg: "bg-gray-100",
  };
}

export default function EvaluationResult() {
  usePageTitle("Resultado da Avaliação");
  const [, navigate] = useLocation();
  const params = useParams<{ id: string }>();
  const pendingId = Number(params.id);
  const { user } = useAuth();
  const userRole = (user as any)?.role;
  const isClientRh = userRole === "client_rh";
  const canViewModuleBreakdown = userRole === "admin";
  const backRoute = isClientRh ? "/meus-resultados" : "/evaluations";

  // V82b: Consultor Canal não tem acesso ao módulo de Avaliações
  // Note: useAuth not available here, guard is on parent Evaluations page

  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [expandedAgents, setExpandedAgents] = useState<Set<string>>(new Set());

  const resultQuery = trpc.evaluations.getResult.useQuery(
    { pendingId },
    {
      enabled: !!pendingId && !isNaN(pendingId),
      refetchOnWindowFocus: false,
    }
  );

  const result = resultQuery.data;

  // Query de Fontes Geradoras (só carrega quando resultado core existe)
  const sourcesQuery = trpc.evaluations.getSourcesGeneratorsResult.useQuery(
    { pendingId },
    {
      enabled: !!pendingId && !isNaN(pendingId) && !!result,
      refetchOnWindowFocus: false,
    }
  );

  // Query de Sugestões
  const suggestionsQuery = trpc.evaluations.getSuggestionsResult.useQuery(
    { pendingId },
    {
      enabled: !!pendingId && !isNaN(pendingId) && !!result,
      refetchOnWindowFocus: false,
    }
  );

  // Query de Resumo OPEN_TEXT
  const openTextQuery = trpc.evaluations.getOpenTextSummary.useQuery(
    { pendingId },
    {
      enabled: !!pendingId && !isNaN(pendingId) && !!result,
      refetchOnWindowFocus: false,
    }
  );

  // Query de Classificação P × S
  const classificationQuery = trpc.evaluations.getClassificationResult.useQuery(
    { evaluationId: pendingId },
    {
      enabled: !!pendingId && !isNaN(pendingId) && !!result,
      refetchOnWindowFocus: false,
    }
  );

  const toggleModule = (moduleCode: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleCode)) {
        next.delete(moduleCode);
      } else {
        next.add(moduleCode);
      }
      return next;
    });
  };

  const expandAll = () => {
    if (result?.moduleBreakdown) {
      setExpandedModules(new Set((result.moduleBreakdown as ModuleBreakdownItem[]).map((m) => m.moduleCode)));
    }
  };

  const collapseAll = () => {
    setExpandedModules(new Set());
  };

  const toggleAgent = (agentKey: string) => {
    setExpandedAgents((prev) => {
      const next = new Set(prev);
      if (next.has(agentKey)) {
        next.delete(agentKey);
      } else {
        next.add(agentKey);
      }
      return next;
    });
  };

  const qStyles = result ? getQualifierStyles(result.qualifier, result.qualifierColorHex) : null;

  return (
    <SalviaLayout title="Resultado da Avaliação">
      <div className="max-w-5xl mx-auto space-y-6 pb-12">
        {/* ─── Header ─── */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(backRoute)}
            className="gap-1.5 shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
          <div>
            <h2 className="text-xl font-bold text-[#002F59] leading-tight">
              Resultado da Avaliação de Risco Psicossocial
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Inventário ERGOS — Análise por Setor e Cargo
            </p>
          </div>
        </div>

        {/* Loading */}
        {resultQuery.isLoading && (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="animate-spin h-10 w-10 text-[#002F59] mr-3" />
            <span className="text-muted-foreground text-lg">Carregando resultado...</span>
          </div>
        )}

        {/* Error */}
        {resultQuery.isError && (
          <div className="flex flex-col items-center justify-center py-24 text-red-500">
            <AlertCircle className="h-12 w-12 mb-3" />
            <p className="font-semibold text-lg">Erro ao carregar resultado</p>
            <p className="text-sm text-muted-foreground mt-1">{resultQuery.error?.message}</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate(backRoute)}>
              Voltar para Avaliações
            </Button>
          </div>
        )}

        {/* Empty */}
        {!resultQuery.isLoading && !resultQuery.isError && !result && (
          <div className="flex flex-col items-center justify-center py-24 text-muted-foreground">
            <Inbox className="h-14 w-14 mb-3 opacity-40" />
            <p className="font-semibold text-lg">Resultado não disponível</p>
            <p className="text-sm mt-1">Esta avaliação ainda não foi processada ou não possui dados suficientes.</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate(backRoute)}>
              Voltar para Avaliações
            </Button>
          </div>
        )}

        {/* ─── Resultado completo ─── */}
        {result && qStyles && (
          <>
            {/* ─── Identificação ─── */}
            <Card className="p-6 border-l-4 border-l-[#002F59]">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-[#002F59]/10 rounded-lg shrink-0">
                  <FileText className="h-6 w-6 text-[#002F59]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-2xl font-bold text-[#002F59] capitalize leading-tight">
                    {result.sector} — {result.jobTitle}
                  </h3>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <span className="font-medium text-foreground">{result.respondentCount}</span> respondentes
                    </span>
                    <span>Template v{result.templateVersion}</span>
                    <span>Matriz de Qualificação v{result.qualificationMatrixVersion}</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* ─── Pontuação principal — 3 cards ─── */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-6 border-l-4 border-l-blue-500">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="p-1.5 bg-blue-100 rounded">
                    <BarChart3 className="h-4 w-4 text-blue-600" />
                  </div>
                  <p className="text-sm font-semibold text-blue-700 uppercase tracking-wide">Grupo A</p>
                </div>
                <p className="text-4xl font-extrabold text-blue-800 tabular-nums">{result.scoreGroupA}</p>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                  Exigências do Trabalho — soma das modas
                </p>
              </Card>

              <Card className="p-6 border-l-4 border-l-indigo-500">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="p-1.5 bg-indigo-100 rounded">
                    <BarChart3 className="h-4 w-4 text-indigo-600" />
                  </div>
                  <p className="text-sm font-semibold text-indigo-700 uppercase tracking-wide">Grupo B</p>
                </div>
                <p className="text-4xl font-extrabold text-indigo-800 tabular-nums">{result.scoreGroupB}</p>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                  Recursos do Trabalho — soma das modas
                </p>
              </Card>

              <Card className={`p-6 border-l-4 ${qStyles.border}`}>
                <div className="flex items-center gap-2.5 mb-3">
                  <div className={`p-1.5 ${qStyles.accentBg} rounded`}>
                    <Activity className="h-4 w-4" style={{ color: qStyles.iconColor }} />
                  </div>
                  <p className={`text-sm font-semibold uppercase tracking-wide ${qStyles.text}`}>
                    Carga Mental
                  </p>
                </div>
                <p className={`text-4xl font-extrabold tabular-nums ${qStyles.text}`}>
                  {result.mentalLoadScore}
                </p>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                  MLS = 0,83 × (A + B)
                </p>
              </Card>
            </div>

            {/* ─── Fórmula ─── */}
            <div className="border border-dashed border-gray-300 rounded-lg px-5 py-4 bg-gray-50/70">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                Fórmula de Carga Mental (MLS)
              </p>
              <code className="text-sm font-mono text-[#002F59] leading-relaxed block">
                MLS = 0,83 × (A + B) = 0,83 × ({result.scoreGroupA} + {result.scoreGroupB}) ={" "}
                <span className="font-bold">{result.mentalLoadScore}</span>
              </code>
            </div>

            {/* ─── Qualificador e Conclusão ─── */}
            <Card className={`overflow-hidden border-l-4 ${qStyles.border}`}>
              {/* Qualificador */}
              <div className={`px-6 py-5 ${qStyles.bg} flex items-center gap-4`}>
                <div className={`p-2 ${qStyles.accentBg} rounded-lg`}>
                  <Target className="h-5 w-5" style={{ color: qStyles.iconColor }} />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Qualificador
                  </p>
                  <p className={`text-2xl font-bold mt-0.5 ${qStyles.text}`}>
                    {result.qualifier}
                  </p>
                </div>
                <div className={`px-5 py-2 rounded-full text-sm font-bold ${qStyles.badgeBg} ${qStyles.badgeText} shadow-sm`}>
                  {result.qualifier}
                </div>
              </div>
              {/* Conclusão */}
              <div className="px-6 py-5 border-t">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                  Conclusão
                </p>
                <p className="text-gray-700 leading-relaxed text-[15px]">{result.conclusionText}</p>
              </div>
            </Card>

            {/* ─── Resumo das Atividades (OPEN_TEXT) ─── */}
            <OpenTextSection openTextQuery={openTextQuery} />

            {/* ─── Classificação de Risco P × S ─── */}
            <ClassificationSection classificationQuery={classificationQuery} />

            {/* ─── Fontes Geradoras ─── */}
            <SourcesGeneratorsSection
              sourcesQuery={sourcesQuery}
              expandedAgents={expandedAgents}
              toggleAgent={toggleAgent}
              canViewAgentDetails={userRole === "admin"}
            />

            {/* ─── Sugestões e Melhorias ─── */}
            <SuggestionsSection suggestionsQuery={suggestionsQuery} />

            {/* ─── Breakdown por módulo ─── */}
            {canViewModuleBreakdown && result.moduleBreakdown && Array.isArray(result.moduleBreakdown) && (
              <Card className="overflow-hidden">
                <div className="px-6 py-5 border-b flex items-center justify-between">
                  <h3 className="text-lg font-bold text-[#002F59]">Detalhamento por Módulo</h3>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={expandAll} className="text-xs h-8">
                      Expandir todos
                    </Button>
                    <Button variant="ghost" size="sm" onClick={collapseAll} className="text-xs h-8">
                      Recolher todos
                    </Button>
                  </div>
                </div>

                <div className="p-6 space-y-8">
                  {/* Grupo A */}
                  <ModuleGroup
                    label="GRUPO A"
                    subtitle="Exigências do Trabalho"
                    total={result.scoreGroupA}
                    colorScheme="blue"
                    modules={(result.moduleBreakdown as ModuleBreakdownItem[]).filter((m) => m.group === "A")}
                    expandedModules={expandedModules}
                    toggleModule={toggleModule}
                  />

                  {/* Separador */}
                  <div className="border-t border-dashed" />

                  {/* Grupo B */}
                  <ModuleGroup
                    label="GRUPO B"
                    subtitle="Recursos do Trabalho"
                    total={result.scoreGroupB}
                    colorScheme="indigo"
                    modules={(result.moduleBreakdown as ModuleBreakdownItem[]).filter((m) => m.group === "B")}
                    expandedModules={expandedModules}
                    toggleModule={toggleModule}
                  />
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </SalviaLayout>
  );
}

/* ─── Sub-componentes ─── */

interface ModuleGroupProps {
  label: string;
  subtitle: string;
  total: number;
  colorScheme: "blue" | "indigo";
  modules: ModuleBreakdownItem[];
  expandedModules: Set<string>;
  toggleModule: (code: string) => void;
}

/* ─── Classificação de Risco P × S Section ─── */

interface ClassificationSectionProps {
  classificationQuery: {
    data?: any;
    isLoading: boolean;
    isError: boolean;
    error?: { message: string } | null;
  };
}

/**
 * V18: Converte horas decimais para formato legível "XhYYmin/dia".
 * Ex: 8.8 → "8h48min/dia", 8 → "8h/dia", 6.5 → "6h30min/dia"
 *
 * PJC-70: `0` é um valor legítimo (colaborador sem hora extra → P1) e passou a
 * ser renderizado como "0h/dia". Antes `hours <= 0` devolvia "Inválida",
 * marcando como erro o caso mais comum da base.
 */
function formatJornadaDisplay(hours: number): string {
  if (hours < 0) return "Inválida";
  if (hours === 0) return "0h/dia";
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `${h}h/dia`;
  return `${h}h${m.toString().padStart(2, "0")}min/dia`;
}

function ClassificationSection({ classificationQuery }: ClassificationSectionProps) {
  const data = classificationQuery.data;

  // Helper para cor do nível de risco
  function getRiskStyles(label: string | null, colorHex: string | null) {
    const l = (label || "").toLowerCase();
    if (l.includes("crítico") || l.includes("critico")) return { bg: "bg-rose-950", text: "text-white", border: "border-rose-900" };
    if (l.includes("muito alto")) return { bg: "bg-red-700", text: "text-white", border: "border-red-700" };
    if (l.includes("alto")) return { bg: "bg-red-500", text: "text-white", border: "border-red-500" };
    if (l.includes("médio-alto") || l.includes("medio-alto")) return { bg: "bg-orange-500", text: "text-white", border: "border-orange-500" };
    if (l.includes("médio") || l.includes("medio")) return { bg: "bg-yellow-500", text: "text-white", border: "border-yellow-500" };
    if (l.includes("baixo")) return { bg: "bg-blue-500", text: "text-white", border: "border-blue-500" };
    if (colorHex) return { bg: "", text: "text-white", border: "border-gray-400", customBg: colorHex };
    return { bg: "bg-gray-400", text: "text-white", border: "border-gray-400" };
  }

  return (
    <Card className="overflow-hidden">
      <div className="px-6 py-5 border-b bg-gradient-to-r from-orange-50/80 to-transparent">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <Zap className="h-5 w-5 text-orange-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-[#002F59]">Classificação de Risco (P × S)</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Probabilidade × Severidade
              {data?.classificationMatrixVersion && (
                <span className="ml-1 font-medium">({data.classificationMatrixVersion})</span>
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Loading */}
        {classificationQuery.isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="animate-spin h-6 w-6 text-orange-500 mr-2" />
            <span className="text-sm text-muted-foreground">Calculando classificação de risco...</span>
          </div>
        )}

        {/* Error */}
        {classificationQuery.isError && (
          <div className="flex items-center gap-2 py-4 text-red-500">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <p className="text-sm">{classificationQuery.error?.message || "Erro ao carregar classificação"}</p>
          </div>
        )}

        {/* Backend error */}
        {data?.error && (
          <div className="flex items-center gap-2 py-4 px-4 bg-amber-50 rounded-lg border border-amber-200">
            <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800">{data.error}</p>
          </div>
        )}

        {/* Result */}
        {data && !data.error && !classificationQuery.isLoading && (
          <div className="space-y-5">
            {/* Grid 3 colunas: Hora Extra | Probabilidade | Severidade */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* PJC-70: Hora Extra — fonte da Probabilidade (P) */}
              <div className="border rounded-lg p-4 bg-slate-50">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Hora Extra Informada</p>
                <p className="text-lg font-bold text-[#002F59]">
                  {data.horaExtraHours != null ? formatJornadaDisplay(data.horaExtraHours) : "Não informada"}
                </p>
                {data.horaExtraRaw && (
                  <p className="text-xs text-muted-foreground mt-1 italic">
                    Fonte: "{data.horaExtraRaw}"
                  </p>
                )}
                {data.jornadaRaw && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Jornada informada: {data.jornadaRaw}
                  </p>
                )}
              </div>

              {/* Probabilidade (P) */}
              <div className="border rounded-lg p-4 bg-blue-50/50">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Probabilidade (P)</p>
                {data.probabilityCode ? (
                  <>
                    <p className="text-lg font-bold text-blue-800">
                      {data.probabilityCode} — {data.probabilityLabel}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Valor: {data.probabilityValue}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">Não calculada (hora extra ausente)</p>
                )}
              </div>

              {/* Severidade (S) */}
              <div className="border rounded-lg p-4 bg-amber-50/50">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Severidade (S)</p>
                {data.severityCode ? (
                  <>
                    <p className="text-lg font-bold text-amber-800">
                      {data.severityCode} — {data.severityLabel}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Peso: {data.severityValue}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">Não calculada</p>
                )}
              </div>
            </div>

            {/* Resultado da Classificação */}
            {data.classificationScore != null && (
              <div className="border-2 rounded-lg overflow-hidden" style={{ borderColor: data.classificationColor || "#6b7280" }}>
                <div className="flex items-center gap-4 px-5 py-4" style={{ backgroundColor: `${data.classificationColor || '#6b7280'}10` }}>
                  <div
                    className="flex items-center justify-center w-16 h-16 rounded-xl text-white font-extrabold text-2xl shadow-md"
                    style={{ backgroundColor: data.classificationColor || "#6b7280" }}
                  >
                    {data.classificationScore}
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Nível de Risco</p>
                    <p className="text-2xl font-bold" style={{ color: data.classificationColor || "#374151" }}>
                      {data.classificationLabel || "Não classificado"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {data.probabilityCode} ({data.probabilityValue}) × {data.severityCode} ({data.severityValue}) = {data.classificationScore}
                    </p>
                  </div>
                  {data.classificationLabel && (() => {
                    const riskStyles = getRiskStyles(data.classificationLabel, data.classificationColor);
                    return (
                      <div
                        className={`px-5 py-2 rounded-full text-sm font-bold shadow-sm ${riskStyles.bg} ${riskStyles.text}`}
                        style={riskStyles.customBg ? { backgroundColor: riskStyles.customBg } : undefined}
                      >
                        {data.classificationLabel}
                      </div>
                    );
                  })()}
                </div>
                {data.classificationConclusion && (
                  <div className="px-5 py-4 border-t bg-white">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Conclusão da Classificação</p>
                    <p className="text-gray-700 leading-relaxed text-[15px]">{data.classificationConclusion}</p>
                  </div>
                )}
              </div>
            )}

            {/* Caso P ou S não calculados */}
            {data.classificationScore == null && (
              <div className="flex items-center gap-2 py-4 px-4 bg-gray-50 rounded-lg border border-gray-200">
                <Info className="h-5 w-5 text-gray-400 shrink-0" />
                <p className="text-sm text-gray-600">
                  Classificação não disponível.
                  {/* PJC-70: comparação com null — `!data.horaExtraHours` daria
                      "não informada" para 0, que é um valor válido (P1). */}
                  {data.horaExtraHours == null && " A hora extra média não foi informada na planilha de importação."}
                  {data.horaExtraHours != null && !data.probabilityCode && " Não foi possível determinar a Probabilidade (P)."}
                  {data.probabilityCode && !data.severityCode && " Não foi possível determinar a Severidade (S)."}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

/* ─── Fontes Geradoras Section ─── */

interface AgentResult {
  agentId: number;
  agentKey: string;
  agentLabel: string;
  sortOrder: number;
  linkedModuleCodes: string[];
  activatedModules: {
    moduleCode: string;
    moduleName: string;
    triggerQuestions: {
      questionCode: string;
      questionText: string | null;
      modeValue: number;
      modeLabel: string | null;
    }[];
  }[];
  hasActivation: boolean;
}

const AGENT_GUIDE_ITEMS = [
  {
    title: "Gestão Organizacional",
    description: "Mostra como a empresa organiza liderança, comunicação, cultura e suporte ao trabalhador no dia a dia.",
  },
  {
    title: "Contexto da Organização do trabalho",
    description: "Explica como o trabalho foi estruturado, distribuído e organizado para que as atividades aconteçam.",
  },
  {
    title: "Características das relações sociais no trabalho",
    description: "Aponta como o clima organizacional e as interações sociais influenciam a experiência de trabalho.",
  },
  {
    title: "Conteúdo das tarefas do trabalho",
    description: "Descreve as características das atividades executadas, incluindo variedade, exigência e repetição.",
  },
  {
    title: "Condição do ambiente do trabalho",
    description: "Considera os aspectos físicos do ambiente que podem afetar conforto, bem-estar e desempenho.",
  },
  {
    title: "Interação pessoa-tarefa",
    description: "Avalia se as exigências do trabalho são compatíveis com as capacidades e recursos do trabalhador.",
  },
  {
    title: "Jornada de trabalho",
    description: "Observa duração da jornada, pausas, turnos e períodos de descanso que influenciam a recuperação.",
  },
] as const;

const SOURCE_GUIDE_ITEMS = [
  {
    title: "Pressão de tempo",
    description: "Avalia o ritmo imposto, a chance de fazer pausas reais e o controle sobre o tempo de trabalho.",
  },
  {
    title: "Atenção",
    description: "Mostra o nível de vigilância exigido, o risco associado ao erro e a necessidade de atenção contínua.",
  },
  {
    title: "Complexidade",
    description: "Indica o quanto a tarefa exige conhecimento, manejo de variáveis e cuidado com as consequências do erro.",
  },
  {
    title: "Monotonia",
    description: "Verifica repetição, pouca variedade no trabalho e baixo estímulo mental durante a rotina.",
  },
  {
    title: "Raciocínio e processos centrais",
    description: "Analisa exigência cognitiva, tomada de decisão, responsabilidade mental e processamento mais profundo.",
  },
  {
    title: "Iniciativa",
    description: "Avalia a autonomia no trabalho, o grau de controle sobre a atividade e a liberdade para agir.",
  },
  {
    title: "Isolamento",
    description: "Mostra o nível de interação social, acesso a suporte e possibilidade de comunicação no trabalho.",
  },
  {
    title: "Horas e turnos de trabalho",
    description: "Observa irregularidade da jornada, organização dos horários e impacto do trabalho na recuperação.",
  },
  {
    title: "Relacionamento no trabalho",
    description: "Avalia a qualidade das relações interpessoais, a integração organizacional e a rede social de trabalho.",
  },
  {
    title: "Demandas gerais",
    description: "Resume o nível global de exigência, responsabilidade e carga geral associada à função.",
  },
] as const;

interface SourcesGeneratorsSectionProps {
  sourcesQuery: {
    data?: { matrixVersionId: number; matrixVersion: string; agents: AgentResult[]; error?: string } | null;
    isLoading: boolean;
    isError: boolean;
    error?: { message: string } | null;
  };
  expandedAgents: Set<string>;
  toggleAgent: (key: string) => void;
  canViewAgentDetails: boolean;
}

function SourcesGeneratorsSection({ sourcesQuery, expandedAgents, toggleAgent, canViewAgentDetails }: SourcesGeneratorsSectionProps) {
  const data = sourcesQuery.data;
  const [showGuideDetails, setShowGuideDetails] = useState(false);

  return (
    <Card className="overflow-hidden">
      <div className="px-6 py-5 border-b bg-gradient-to-r from-[#002F59]/5 to-transparent">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#002F59]/10 rounded-lg">
            <ShieldAlert className="h-5 w-5 text-[#002F59]" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-[#002F59]">Agentes, Fontes e Circunstâncias</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Matriz de Fontes Geradoras
              {data?.matrixVersion && data.matrixVersion !== "N/A" && (
                <span className="ml-1 font-medium">({data.matrixVersion})</span>
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Loading */}
        {sourcesQuery.isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="animate-spin h-6 w-6 text-[#002F59] mr-2" />
            <span className="text-sm text-muted-foreground">Calculando fontes geradoras...</span>
          </div>
        )}

        {/* Error */}
        {sourcesQuery.isError && (
          <div className="flex items-center gap-2 py-4 text-red-500">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <p className="text-sm">{sourcesQuery.error?.message || "Erro ao carregar fontes geradoras"}</p>
          </div>
        )}

        {/* Error from backend (no matrix published) */}
        {data?.error && (
          <div className="flex items-center gap-2 py-4 px-4 bg-amber-50 rounded-lg border border-amber-200">
            <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800">{data.error}</p>
          </div>
        )}

        {/* Guide */}
        {!sourcesQuery.isLoading && !data?.error && (
          <div className="mb-5 overflow-hidden rounded-2xl border border-sky-200 bg-gradient-to-r from-sky-50 to-slate-50 shadow-sm">
            <div className="flex items-start gap-3 px-4 py-4 sm:px-5">
              <div className="mt-0.5 rounded-full bg-white/80 p-2 text-sky-700 shadow-sm ring-1 ring-sky-100">
                <Info className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1 space-y-3">
                <div>
                  <p className="text-sm font-semibold text-[#002F59]">Como interpretar esta seção</p>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">
                    Esta área resume <strong>o que foi identificado na rotina de trabalho</strong> e ajuda a entender por que o risco psicossocial apareceu neste resultado.
                  </p>
                </div>

                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded-xl border border-white/70 bg-white/70 p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-700">Agentes</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-700">
                      São as <strong>dimensões do trabalho avaliadas pelo ERGOS</strong>, como gestão, jornada, ambiente e interação pessoa-tarefa.
                    </p>
                  </div>
                  <div className="rounded-xl border border-white/70 bg-white/70 p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-700">Fontes e circunstâncias</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-700">
                      São os <strong>fatores que ajudam a explicar por que o risco aparece</strong> na rotina, como pressão de tempo, monotonia, isolamento ou jornada.
                    </p>
                  </div>
                  <div className="rounded-xl border border-white/70 bg-white/70 p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-700">Como ler o resultado</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-700">
                      Cada linha mostra um agente avaliado e, ao lado, as <strong>fontes identificadas como mais relevantes</strong> para aquele contexto.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-2 border-t border-sky-100 pt-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs leading-relaxed text-slate-500">
                    {canViewAgentDetails
                      ? "Quando você expandir um agente, verá as perguntas que contribuíram para ativar cada fonte geradora."
                      : "Este bloco mantém apenas a explicação dos conceitos. O detalhamento das respostas fica disponível somente para o perfil ADM."}
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowGuideDetails((prev) => !prev)}
                    className="inline-flex items-center gap-1 self-start text-sm font-medium text-sky-700 underline-offset-4 transition hover:text-sky-800 hover:underline"
                  >
                    {showGuideDetails ? "Ocultar explicação completa" : "Ver explicação completa"}
                    {showGuideDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                </div>

                {showGuideDetails && (
                  <div className="space-y-4 rounded-xl border border-sky-100 bg-white/80 p-4 text-sm leading-relaxed text-slate-700">
                    <div>
                      <p>
                        No método ERGOS, os agentes representam elementos do trabalho que podem gerar estresse, sobrecarga, sofrimento psíquico e adoecimento. Já as fontes e circunstâncias mostram como esse risco se manifesta na prática, considerando exigência mental, autonomia, relações, organização do tempo e recuperação.
                      </p>
                      <p className="mt-2">
                        Em termos práticos, use esta leitura: <strong>o agente indica a dimensão analisada</strong>; <strong>a fonte ou circunstância mostra o fator de atenção dentro dessa dimensão</strong>; e os detalhes expandidos ajudam a entender quais respostas sustentaram esse resultado.
                      </p>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="rounded-full bg-sky-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-700">Agentes avaliados</span>
                        <p className="text-xs text-slate-500">Cada agente representa uma dimensão importante da rotina de trabalho.</p>
                      </div>
                      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {AGENT_GUIDE_ITEMS.map((item) => (
                          <div key={item.title} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                            <p className="text-sm font-semibold text-[#002F59]">{item.title}</p>
                            <p className="mt-1 text-sm leading-relaxed text-slate-600">{item.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="rounded-full bg-sky-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-700">Fontes e circunstâncias</span>
                        <p className="text-xs text-slate-500">Esses fatores ajudam a explicar como o risco aparece no dia a dia.</p>
                      </div>
                      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {SOURCE_GUIDE_ITEMS.map((item) => (
                          <div key={item.title} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                            <p className="text-sm font-semibold text-[#002F59]">{item.title}</p>
                            <p className="mt-1 text-sm leading-relaxed text-slate-600">{item.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Agents list */}
        {data && !data.error && data.agents && data.agents.length > 0 && (
          <div className="space-y-3">
            {data.agents.map((agent: AgentResult) => (
              <div
                key={agent.agentKey}
                className={`border rounded-lg overflow-hidden transition-colors ${
                  agent.hasActivation ? "border-red-200 bg-red-50/30" : "border-gray-200"
                }`}
              >
                {/* Agent header */}
                <button
                  onClick={() => canViewAgentDetails && agent.hasActivation && toggleAgent(agent.agentKey)}
                  className={`w-full flex items-center justify-between px-4 py-3.5 text-left transition-colors ${
                    canViewAgentDetails && agent.hasActivation ? "hover:bg-red-50/60 cursor-pointer" : "cursor-default"
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {agent.hasActivation ? (
                      <div className="p-1 bg-red-100 rounded">
                        <Zap className="h-4 w-4 text-red-600" />
                      </div>
                    ) : (
                      <div className="p-1 bg-emerald-100 rounded">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold truncate ${
                        agent.hasActivation ? "text-red-800" : "text-foreground"
                      }`}>
                        {agent.agentLabel}
                      </p>
                      {!agent.hasActivation && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Nenhuma fonte geradora identificada
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {/* Module chips */}
                    {agent.hasActivation && (
                      <div className="flex flex-wrap gap-1.5 justify-end max-w-[400px]">
                        {agent.activatedModules.map((mod) => (
                          <span
                            key={mod.moduleCode}
                            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200"
                          >
                            {mod.moduleName}
                          </span>
                        ))}
                      </div>
                    )}
                    {canViewAgentDetails && agent.hasActivation && (
                      expandedAgents.has(agent.agentKey) ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground ml-2" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground ml-2" />
                      )
                    )}
                  </div>
                </button>

                {/* Expanded: trigger details */}
                {canViewAgentDetails && agent.hasActivation && expandedAgents.has(agent.agentKey) && (
                  <div className="border-t border-red-200">
                    {agent.activatedModules.map((mod) => (
                      <div key={mod.moduleCode} className="px-4 py-3 border-b border-red-100 last:border-b-0">
                        <p className="text-xs font-semibold text-red-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                          <Zap className="h-3 w-3" />
                          {mod.moduleName}
                          <span className="text-red-400 font-normal normal-case tracking-normal">
                            — {mod.triggerQuestions.length} pergunta(s) com moda = 4
                          </span>
                        </p>
                        <div className="space-y-1.5 ml-4">
                          {mod.triggerQuestions.map((tq) => (
                            <div key={tq.questionCode} className="flex items-start gap-2 text-sm">
                              <XCircle className="h-3.5 w-3.5 text-red-400 mt-0.5 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-gray-700 leading-snug">
                                  {tq.questionText || (
                                    <span className="italic text-muted-foreground">{tq.questionCode}</span>
                                  )}
                                </p>
                                <p className="text-[11px] text-muted-foreground/60 mt-0.5">
                                  moda: {tq.modeValue}
                                  {tq.modeLabel && ` • "${tq.modeLabel}"`}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Summary footer */}
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg text-sm">
              <span className="text-muted-foreground">
                {data.agents.filter((a: AgentResult) => a.hasActivation).length} de {data.agents.length} agentes com fontes geradoras identificadas
              </span>
              <span className="text-xs text-muted-foreground/70">
                Matriz {data.matrixVersion}
              </span>
            </div>
          </div>
        )}

        {/* No agents at all */}
        {data && !data.error && (!data.agents || data.agents.length === 0) && (
          <div className="flex items-center gap-2 py-4 text-muted-foreground">
            <Inbox className="h-5 w-5 opacity-40" />
            <p className="text-sm">Nenhum agente encontrado na Matriz de Fontes Geradoras.</p>
          </div>
        )}
      </div>
    </Card>
  );
}

function ModuleGroup({ label, subtitle, total, colorScheme, modules, expandedModules, toggleModule }: ModuleGroupProps) {
  const colors = colorScheme === "blue"
    ? { labelBg: "bg-blue-100", labelText: "text-blue-700", scoreBg: "bg-blue-50", scoreText: "text-blue-800", modaBg: "bg-blue-100", modaText: "text-blue-800", totalText: "text-blue-800" }
    : { labelBg: "bg-indigo-100", labelText: "text-indigo-700", scoreBg: "bg-indigo-50", scoreText: "text-indigo-800", modaBg: "bg-indigo-100", modaText: "text-indigo-800", totalText: "text-indigo-800" };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <span className={`text-xs font-bold px-3 py-1.5 rounded-md ${colors.labelBg} ${colors.labelText} tracking-wide`}>
          {label}
        </span>
        <span className="text-sm text-muted-foreground">{subtitle}</span>
        <span className={`ml-auto text-sm font-bold ${colors.totalText}`}>
          Total: {total}
        </span>
      </div>

      <div className="space-y-2">
        {modules.map((mod) => (
          <div key={mod.moduleCode} className="border rounded-lg overflow-hidden">
            <button
              onClick={() => toggleModule(mod.moduleCode)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50/80 transition-colors text-left"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-sm font-semibold text-foreground truncate">{mod.moduleName}</span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {mod.questions.length} {mod.questions.length === 1 ? "pergunta" : "perguntas"}
                </span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className={`text-sm font-bold px-3 py-1 rounded-md ${colors.scoreBg} ${colors.scoreText} tabular-nums`}>
                  {mod.moduleScore}
                </span>
                {expandedModules.has(mod.moduleCode) ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
            </button>

            {expandedModules.has(mod.moduleCode) && (
              <div className="border-t">
                {/* Header da tabela */}
                <div className="grid grid-cols-[1fr_80px_1fr] gap-2 px-4 py-2.5 bg-gray-100/70 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  <span>Pergunta</span>
                  <span className="text-center">Moda</span>
                  <span>Resposta</span>
                </div>
                {/* Linhas */}
                {mod.questions.map((q, idx) => (
                  <div
                    key={q.questionCode}
                    className={`grid grid-cols-[1fr_80px_1fr] gap-2 px-4 py-3 items-start ${
                      idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                    } ${idx > 0 ? "border-t border-gray-100" : ""}`}
                  >
                    {/* Pergunta: texto real + código em subtexto */}
                    <div className="min-w-0">
                      <p className="text-sm text-foreground leading-snug">
                        {q.questionText || (
                          <span className="italic text-muted-foreground">
                            Pergunta não encontrada ({q.questionCode})
                          </span>
                        )}
                      </p>

                    </div>
                    {/* Moda */}
                    <div className="flex justify-center pt-0.5">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-md font-bold text-sm ${colors.modaBg} ${colors.modaText}`}>
                        {q.modeValue}
                      </span>
                    </div>
                    {/* Descrição da resposta */}
                    <div className="pt-0.5">
                      <p className="text-sm text-muted-foreground leading-snug">
                        {q.modeLabel || "—"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


/* ─── Sugestões e Melhorias dos ERGOS ─── */

interface SuggestionsSectionProps {
  suggestionsQuery: {
    data?: {
      suggestionsVersionId: number;
      suggestionsVersionLabel: string;
      modules: Array<{
        moduleCode: string;
        moduleName: string;
        moduleTotal: number;
        suggestionType: "PREVENTION" | "MITIGATION_ELIMINATION" | "NOT_APPLICABLE";
        suggestions: Array<{
          itemId: number;
          title: string;
          recommendationText: string;
          recommendationType: string;
        }>;
        message: string | null;
      }>;
      error?: string;
    };
    isLoading: boolean;
    isError: boolean;
    error?: { message: string } | null;
  };
}

function getSuggestionTypeConfig(type: string) {
  switch (type) {
    case "PREVENTION":
      return {
        label: "Prevenção",
        icon: Shield,
        bg: "bg-blue-50",
        border: "border-blue-200",
        text: "text-blue-800",
        badgeBg: "bg-blue-600",
        badgeText: "text-white",
        iconColor: "text-blue-600",
      };
    case "MITIGATION_ELIMINATION":
      return {
        label: "Mitigação / Eliminação",
        icon: ShieldAlert,
        bg: "bg-red-50",
        border: "border-red-200",
        text: "text-red-800",
        badgeBg: "bg-red-600",
        badgeText: "text-white",
        iconColor: "text-red-600",
      };
    default:
      return {
        label: "Sem sugestão",
        icon: Ban,
        bg: "bg-gray-50",
        border: "border-gray-200",
        text: "text-gray-500",
        badgeBg: "bg-gray-400",
        badgeText: "text-white",
        iconColor: "text-gray-400",
      };
  }
}

function SuggestionsSection({ suggestionsQuery }: SuggestionsSectionProps) {
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());

  const toggleModule = (moduleCode: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleCode)) {
        next.delete(moduleCode);
      } else {
        next.add(moduleCode);
      }
      return next;
    });
  };

  if (suggestionsQuery.isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="animate-spin h-5 w-5" />
          <span>Carregando sugestões...</span>
        </div>
      </Card>
    );
  }

  if (suggestionsQuery.isError) {
    return (
      <Card className="p-6 border-red-200 bg-red-50">
        <div className="flex items-center gap-3 text-red-600">
          <AlertCircle className="h-5 w-5" />
          <span>Erro ao carregar sugestões: {suggestionsQuery.error?.message}</span>
        </div>
      </Card>
    );
  }

  const data = suggestionsQuery.data;
  if (!data || data.error) {
    return (
      <Card className="p-6 border-amber-200 bg-amber-50">
        <div className="flex items-center gap-3 text-amber-700">
          <Info className="h-5 w-5" />
          <span>{data?.error || "Sugestões não disponíveis para esta avaliação."}</span>
        </div>
      </Card>
    );
  }

  if (!data.modules || data.modules.length === 0) {
    return null;
  }

  // Separar módulos por tipo
  const withSuggestions = data.modules.filter((m) => m.suggestionType !== "NOT_APPLICABLE");
  const withoutSuggestions = data.modules.filter((m) => m.suggestionType === "NOT_APPLICABLE");

  return (
    <Card className="overflow-hidden">
      {/* Header */}
      <div className="px-6 py-5 border-b">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
            <Lightbulb className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-[#002F59]">Sugestões e Melhorias dos ERGOS</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Matriz de Sugestões ({data.suggestionsVersionLabel})
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-4">
        {/* Módulos com sugestões */}
        {withSuggestions.map((mod) => {
          const config = getSuggestionTypeConfig(mod.suggestionType);
          const TypeIcon = config.icon;
          const isExpanded = expandedModules.has(mod.moduleCode);

          return (
            <div
              key={mod.moduleCode}
              className={`rounded-lg border ${config.border} ${config.bg} overflow-hidden`}
            >
              {/* Module header (clicável) */}
              <button
                onClick={() => toggleModule(mod.moduleCode)}
                className="w-full px-5 py-4 flex items-center justify-between hover:opacity-90 transition-opacity"
              >
                <div className="flex items-center gap-3">
                  <TypeIcon className={`h-5 w-5 ${config.iconColor}`} />
                  <div className="text-left">
                    <p className={`font-semibold ${config.text}`}>{mod.moduleName}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Pontuação: {mod.moduleTotal}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold ${config.badgeBg} ${config.badgeText}`}
                  >
                    {config.label}
                  </span>
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
              </button>

              {/* Conteúdo expandido */}
              {isExpanded && (
                <div className="px-5 pb-4 space-y-3">
                  {mod.suggestions.length > 0 ? (
                    mod.suggestions.map((sug) => (
                      <div
                        key={sug.itemId}
                        className="bg-white rounded-md p-4 border border-gray-100"
                      >
                        <p className="font-medium text-gray-900 text-sm">{sug.title}</p>
                        <p className="text-gray-600 text-sm mt-1.5 leading-relaxed whitespace-pre-line">
                          {sug.recommendationText}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="bg-white rounded-md p-4 border border-gray-100">
                      <p className="text-gray-500 text-sm italic">
                        {mod.message || "Sem recomendação cadastrada para este tipo."}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Módulos sem sugestão (NOT_APPLICABLE) */}
        {withoutSuggestions.length > 0 && (
          <div className="mt-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Módulos sem necessidade de intervenção
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {withoutSuggestions.map((mod) => (
                <div
                  key={mod.moduleCode}
                  className="flex items-center gap-2 px-3 py-2 rounded-md bg-gray-50 border border-gray-100"
                >
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700">{mod.moduleName}</p>
                    <p className="text-xs text-muted-foreground">
                      Pontuação: {mod.moduleTotal} (abaixo de 4)
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}


// ─── OpenTextSection ─────────────────────────────────────────────────
interface OpenTextSectionProps {
  openTextQuery: ReturnType<typeof trpc.evaluations.getOpenTextSummary.useQuery>;
}

function OpenTextSection({ openTextQuery }: OpenTextSectionProps) {
  const [showRaw, setShowRaw] = useState(false);

  if (openTextQuery.isLoading || openTextQuery.isFetching) {
    return (
      <Card className="overflow-hidden">
        <div className="px-6 py-5 border-b bg-gradient-to-r from-violet-50 to-purple-50">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-violet-100">
              <MessageSquareText className="h-5 w-5 text-violet-700" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#002F59]">Resumo das Atividades</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Carregando...</p>
            </div>
          </div>
        </div>
        <div className="px-6 py-8 flex flex-col items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin text-violet-500" />
          <span className="text-sm">Processando resumo das atividades...</span>
          <span className="text-xs text-gray-400">Isso pode levar alguns segundos</span>
        </div>
      </Card>
    );
  }

  if (openTextQuery.isError) {
    return (
      <Card className="overflow-hidden">
        <div className="px-6 py-5 border-b bg-gradient-to-r from-violet-50 to-purple-50">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-violet-100">
              <MessageSquareText className="h-5 w-5 text-violet-700" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#002F59]">Resumo das Atividades</h3>
            </div>
          </div>
        </div>
        <div className="px-6 py-5">
          <div className="flex items-center gap-2 p-4 rounded-lg bg-red-50 border border-red-100">
            <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-700">Erro ao carregar resumo</p>
              <p className="text-xs text-red-600 mt-0.5">Tente recarregar a página ou clique no botão abaixo.</p>
            </div>
            <button
              onClick={() => openTextQuery.refetch()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-md border border-violet-200 transition-colors"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Recarregar
            </button>
          </div>
        </div>
      </Card>
    );
  }

  const data = openTextQuery.data;
  if (!data) return null;

  return (
    <Card className="overflow-hidden">
      {/* Header */}
      <div className="px-6 py-5 border-b bg-gradient-to-r from-violet-50 to-purple-50">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-violet-100">
            <MessageSquareText className="h-5 w-5 text-violet-700" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-[#002F59]">
              Resumo das Atividades
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Pergunta inicial qualitativa — {data.responsesCount} resposta{data.responsesCount !== 1 ? "s" : ""} recebida{data.responsesCount !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
      </div>

      {/* Pergunta */}
      <div className="px-6 pt-5 pb-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
          Pergunta aplicada
        </p>
        <p className="text-sm text-gray-700 italic leading-relaxed">
          "{data.questionLabel}"
        </p>
      </div>

      {/* Status: Nenhuma resposta */}
      {data.status === "NO_RESPONSES" && (
        <div className="px-6 pb-5">
          <div className="flex items-center gap-2 p-4 rounded-lg bg-gray-50 border border-gray-100">
            <Inbox className="h-5 w-5 text-gray-400 shrink-0" />
            <p className="text-sm text-gray-500">
              Nenhuma resposta informada pelos colaboradores para este setor/cargo.
            </p>
          </div>
        </div>
      )}

      {/* Status: Erro */}
      {data.status === "ERROR" && (
        <div className="px-6 pb-5">
          <div className="flex items-center gap-2 p-4 rounded-lg bg-red-50 border border-red-100">
            <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-700">Resumo indisponível</p>
              <p className="text-xs text-red-600 mt-0.5">
                {data.errorMessage || "Erro ao processar o resumo das atividades."}
              </p>
            </div>
            <button
              onClick={() => openTextQuery.refetch()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-md border border-violet-200 transition-colors"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Tentar novamente
            </button>
          </div>
          {/* Mesmo com erro, mostrar respostas originais se existirem */}
          {data.rawResponses && data.rawResponses.length > 0 && (
            <div className="mt-4">
              <RawResponsesAccordion
                responses={data.rawResponses}
                showRaw={showRaw}
                setShowRaw={setShowRaw}
              />
            </div>
          )}
        </div>
      )}

      {/* Status: Sucesso com summaryText */}
      {data.status === "SUCCESS" && data.summaryText && (
        <div className="px-6 pb-5 space-y-4">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              Resumo consolidado
            </p>
            <div className="p-4 rounded-lg bg-violet-50/50 border border-violet-100">
              <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">
                {data.summaryText}
              </p>
            </div>
          </div>

          {data.rawResponses && data.rawResponses.length > 0 && (
            <RawResponsesAccordion
              responses={data.rawResponses}
              showRaw={showRaw}
              setShowRaw={setShowRaw}
            />
          )}
        </div>
      )}

      {/* Status: Sucesso mas responsesCount>0 e summaryText vazio (edge case) */}
      {data.status === "SUCCESS" && !data.summaryText && data.responsesCount > 0 && (
        <div className="px-6 pb-5">
          <div className="flex items-center gap-2 p-4 rounded-lg bg-amber-50 border border-amber-100">
            <Loader2 className="h-5 w-5 text-amber-500 animate-spin shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-700">Resumo em processamento</p>
              <p className="text-xs text-amber-600 mt-0.5">
                O resumo está sendo gerado. Recarregue em alguns instantes.
              </p>
            </div>
            <button
              onClick={() => openTextQuery.refetch()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-md border border-violet-200 transition-colors"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Recarregar
            </button>
          </div>
          {data.rawResponses && data.rawResponses.length > 0 && (
            <div className="mt-4">
              <RawResponsesAccordion
                responses={data.rawResponses}
                showRaw={showRaw}
                setShowRaw={setShowRaw}
              />
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ─── Sub-component: Accordion de respostas originais ─────────────────
interface RawResponsesAccordionProps {
  responses: string[];
  showRaw: boolean;
  setShowRaw: (v: boolean) => void;
}

function RawResponsesAccordion({ responses, showRaw, setShowRaw }: RawResponsesAccordionProps) {
  return (
    <div>
      <button
        onClick={() => setShowRaw(!showRaw)}
        className="flex items-center gap-2 text-xs font-medium text-violet-700 hover:text-violet-900 transition-colors"
      >
        {showRaw ? (
          <>
            <EyeOff className="h-3.5 w-3.5" />
            Ocultar respostas originais
          </>
        ) : (
          <>
            <Eye className="h-3.5 w-3.5" />
            Ver respostas originais ({responses.length})
          </>
        )}
      </button>
      {showRaw && (
        <div className="mt-2 space-y-2">
          {responses.map((resp, idx) => (
            <div
              key={idx}
              className="flex gap-3 p-3 rounded-md bg-gray-50 border border-gray-100"
            >
              <span className="text-xs font-semibold text-violet-600 shrink-0 mt-0.5">
                #{idx + 1}
              </span>
              <p className="text-sm text-gray-700 leading-relaxed">{resp}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
